import { DEBUG, HUD } from '../config/tuning';
import { bus } from '../core/EventBus';
import type { PostStack } from '../render/PostStack';
import { POST_EFFECT_KEYS, POST_EFFECT_LABELS } from '../render/PostSettings';

/**
 * The downlink tuning panel, shown alongside the debug overlay (`~`).
 *
 * Two jobs, both required by M4. First, every one of the ten effects of SPEC §3.3.2 has to be
 * individually toggleable, and until the settings screen exists at M10 this is where that lives.
 * Second, the whole link-quality breakdown hangs off one value that the simulation will not drive
 * properly until M5 — so this panel can seize it and sweep it from 1 to 0 by hand, which is the
 * only way to judge whether the progressive breakdown is convincing before there is anything in
 * the world to jam the link.
 *
 * The override is deliberately sticky and clearly labelled: a held link quality that is silently
 * still in force is exactly the kind of thing that costs an hour of debugging at M5.
 */

const PANEL_STYLE = `
  position: fixed; top: 12px; right: 12px; z-index: 20;
  font: 11px/1.5 ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  letter-spacing: 0.04em; text-transform: uppercase;
  color: ${HUD.colour}; background: rgba(4, 10, 12, 0.82);
  border: 1px solid rgba(63, 232, 216, 0.35); border-radius: 2px;
  padding: 10px 12px; width: 210px;
`;

/** Link-quality steps taken by the [ and ] keys. */
const LINK_STEP = 0.05;

export class PostPanel {
  private readonly post: PostStack;
  private readonly root: HTMLDivElement;
  private readonly slider: HTMLInputElement;
  private readonly linkReadout: HTMLSpanElement;
  private readonly stateLine: HTMLDivElement;
  private readonly checkboxes = new Map<string, HTMLInputElement>();

  private override: number | null = null;
  private simulatedLink = 1;
  private lastTextUpdateMs = 0;

  constructor(post: PostStack, parent: HTMLElement = document.body) {
    this.post = post;

    this.root = document.createElement('div');
    this.root.setAttribute('style', PANEL_STYLE);
    this.root.style.display = 'none';

    const title = document.createElement('div');
    title.textContent = 'DOWNLINK · §3.3.2';
    title.style.opacity = '0.65';
    title.style.marginBottom = '8px';
    this.root.appendChild(title);

    // --- Link quality, the one value the whole breakdown hangs off.
    const linkCaption = document.createElement('div');
    linkCaption.style.display = 'flex';
    linkCaption.style.justifyContent = 'space-between';
    const linkName = document.createElement('span');
    linkName.textContent = 'LINK';
    this.linkReadout = document.createElement('span');
    linkCaption.append(linkName, this.linkReadout);
    this.root.appendChild(linkCaption);

    this.slider = document.createElement('input');
    this.slider.type = 'range';
    this.slider.min = '0';
    this.slider.max = '1';
    this.slider.step = '0.01';
    this.slider.value = '1';
    this.slider.style.width = '100%';
    this.slider.style.accentColor = HUD.colour;
    this.slider.addEventListener('input', () => {
      this.override = Number(this.slider.value);
    });
    this.root.appendChild(this.slider);

    this.stateLine = document.createElement('div');
    this.stateLine.style.opacity = '0.6';
    this.stateLine.style.marginBottom = '8px';
    this.root.appendChild(this.stateLine);

    // --- The ten effects, in the order of SPEC §3.3.2, plus FXAA.
    for (const key of POST_EFFECT_KEYS) {
      const row = document.createElement('label');
      row.style.display = 'flex';
      row.style.gap = '6px';
      row.style.alignItems = 'center';

      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = post.settings[key];
      box.style.accentColor = HUD.colour;
      box.addEventListener('change', () => {
        post.settings[key] = box.checked;
      });
      this.checkboxes.set(key, box);

      const name = document.createElement('span');
      name.textContent = POST_EFFECT_LABELS[key];
      row.append(box, name);
      this.root.appendChild(row);
    }

    const help = document.createElement('div');
    help.textContent = '[ ] SWEEP LINK · L RELEASE';
    help.style.opacity = '0.5';
    help.style.marginTop = '8px';
    this.root.appendChild(help);

    parent.appendChild(this.root);

    // One key shows both debug surfaces; the overlay owns `~`.
    bus.on('debug:visibility', ({ visible }) => {
      this.root.style.display = visible ? 'block' : 'none';
    });
    window.addEventListener('keydown', this.onKeyDown);
  }

  /** Link quality to drive the stack with: the override when held, else the simulated value. */
  linkQualityFor(simulatedLink: number): number {
    this.simulatedLink = simulatedLink;
    return this.override ?? simulatedLink;
  }

  /** True while the panel is holding link quality away from the simulation. */
  get isOverridden(): boolean {
    return this.override !== null;
  }

  /** Refresh the readouts. Throttled like the debug overlay: building strings allocates. */
  update(): void {
    if (this.root.style.display === 'none') return;
    const nowMs = performance.now();
    if (nowMs - this.lastTextUpdateMs < 1000 / DEBUG.TEXT_UPDATE_HZ) return;
    this.lastTextUpdateMs = nowMs;

    const link = this.override ?? this.simulatedLink;
    if (this.override === null) this.slider.value = String(link);
    this.linkReadout.textContent = link.toFixed(2);

    const exposure = this.post.exposureStops;
    this.stateLine.textContent =
      `${this.override === null ? 'SIM' : 'HELD'} · GAIN ${exposure >= 0 ? '+' : '−'}` +
      `${Math.abs(exposure).toFixed(1)}EV · ${this.post.passCount} PASS`;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.root.remove();
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat && event.code === 'KeyL') return;

    switch (event.code) {
      case 'BracketLeft':
        this.setOverride((this.override ?? this.simulatedLink) - LINK_STEP);
        break;
      case 'BracketRight':
        this.setOverride((this.override ?? this.simulatedLink) + LINK_STEP);
        break;
      case 'KeyL':
        this.override = null;
        break;
      default:
        return;
    }
    this.lastTextUpdateMs = 0;
  };

  private setOverride(value: number): void {
    const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
    this.override = clamped;
    this.slider.value = String(clamped);
  }
}
